









































































































import { Component, Ref } from "vue-property-decorator";
import router from "@/router/router";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import PdfTemplateFieldMappingDialogComponent from "@/components/admin/clients/PdfTemplateFieldMappingDialog.vue";
import PdfTemplateFieldTableComponent from "@/components/admin/clients/PdfTemplateFieldTable.vue";
import PdfTemplateActionsComponent from "@/components/admin/clients/PdfTemplateActions.vue";
import PdfTemplateImportDialogComponent from "@/components/admin/clients/PdfTemplateImportDialog.vue";
import {
  PdfTemplateFieldMapping,
  PdfTemplateForm,
  PdfTemplateFormFieldsUnavailable,
  PdfTemplateRow,
} from "@/data/models/ClientAdministrationRow";
import {
  getClientForAdministration,
  getClientPdfTemplateFormForAdministration,
  getClientPdfTemplatesForAdministration,
  saveClientPdfTemplateMappingForAdministration,
} from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { clientDetailTitle, clientPageTitle } from "@/utils/ClientAdministrationDetail";
import { rememberClientTitle, setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  buildEditorRows,
  cloudProblemStatus,
  describeMappingEdits,
  filterEditorRows,
  isLinked,
  mappingsFromTemplateFields,
  mappingsKey,
  mappingsToSave,
  PDF_TEMPLATE_FIELD_FILTERS,
  PdfTemplateEditorRow,
  PdfTemplateFieldFilter,
  PdfTemplateMessagePart,
  PdfTemplateScope,
  PdfTemplateValueOption,
  saveConfirmationParts,
  saveErrorMessage,
  scopeNoticeParts,
  valueOptionsByName,
} from "@/utils/PdfTemplateMapping";
import { applyMappingImport, MappingImportSource, mappingImportSources } from "@/utils/PdfTemplateFiles";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * One PDF template in the admin portal, and its mapping editor: every form field of the PDF with
 * the category and values linked to it and the text placed before or after them. BEARBEITEN makes
 * the rows clickable; a row opens the field's mapping dialog, and SICHERN saves every change at once.
 * The file actions (download, preview, replace, rename, copy, delete) are `PdfTemplateActions`.
 *
 * IRES Admin still edits templates too and saves them whole, so IRES Cloud refuses a save when the
 * template was saved since this page loaded it ("changed meanwhile"); the user then reloads.
 *
 * Problem flags (values IRES Cloud leaves blank) show on the HyDry certificate's template only: IRES
 * Cloud fills no other template; IRES Project fills them, and knows every value offered here.
 *
 * Addressed by `templateId` plus the list's scope (`clientId`, or `shared=1`), so a reload or a shared
 * link works. **Not the access boundary**: both calls repeat the portal and `MANAGE_CLIENTS` checks.
 */
@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "field-mapping-dialog": PdfTemplateFieldMappingDialogComponent,
    "field-table": PdfTemplateFieldTableComponent,
    "template-actions": PdfTemplateActionsComponent,
    "import-dialog": PdfTemplateImportDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientAdministrationPdfTemplateComponent extends SettingsPageMixin {
  form: PdfTemplateForm | null = null;
  rows: PdfTemplateEditorRow[] = [];
  /** The mapping as loaded, for "has anything changed" and the confirmation's list of changes. */
  initialMappings: PdfTemplateFieldMapping[] = [];
  initialKey = "";
  /** The client's name and number, as its detail screen titles it; null for shared or when unknown. */
  clientLabel: string | null = null;
  filter: PdfTemplateFieldFilter = "ALL";
  isLoadingTemplate = true;
  loadError = false;

  readonly filters = PDF_TEMPLATE_FIELD_FILTERS;

  @Ref("mappingDialog")
  mappingDialog!: PdfTemplateFieldMappingDialogComponent;

  @Ref("importDialog")
  importDialog!: PdfTemplateImportDialogComponent;

  get clientId(): string | null {
    const value = this.$route.query.clientId;
    return typeof value === "string" && value !== "" ? value : null;
  }

  get isShared(): boolean {
    return this.$route.query.shared === "1";
  }

  get templateId(): string {
    const value = this.$route.query.templateId;
    return typeof value === "string" ? value : "";
  }

  get template(): PdfTemplateRow | null {
    return this.form?.template ?? null;
  }

  get formFieldsUnavailable(): PdfTemplateFormFieldsUnavailable | null {
    return this.form?.formFieldsUnavailable ?? null;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get title(): string {
    return this.template?.name || this.template?.fileName || "–";
  }

  get isCertificateTemplate(): boolean {
    return this.form?.isHyDryCertificateTemplate === true;
  }

  get canEdit(): boolean {
    return this.template !== null;
  }

  get options(): Map<string, PdfTemplateValueOption> {
    return valueOptionsByName(this.form?.catalogue ?? [], this.template?.fields ?? []);
  }

  get visibleRows(): PdfTemplateEditorRow[] {
    return filterEditorRows(this.rows, this.filter);
  }

  /** What SICHERN would save now; the preview shows it in edit mode. */
  get currentMappings(): PdfTemplateFieldMapping[] {
    return mappingsToSave(this.rows);
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get hasChanged(): boolean {
    return mappingsKey(mappingsToSave(this.rows)) !== this.initialKey;
  }

  get linkedLabel(): string {
    return this.$t("admin.pdfTemplates.edit.linkedCount", [this.rows.filter(isLinked).length, this.rows.length]).toString();
  }

  /** Linked values IRES Cloud leaves blank in the certificate, as the mapping stands now. */
  get problemCount(): number {
    return this.rows
      .filter(isLinked)
      .flatMap((row) => row.mapping?.values ?? [])
      .filter((value) => cloudProblemStatus(value, this.options, this.isCertificateTemplate) !== null).length;
  }

  /** The client the template belongs to, by name if known, else by id. */
  get scopeName(): string {
    return this.clientLabel || this.clientId || "–";
  }

  get scope(): PdfTemplateScope {
    return { isShared: this.isShared, isCertificateTemplate: this.isCertificateTemplate, clientName: this.scopeName };
  }

  get scopeNotice(): string {
    return this.translate(scopeNoticeParts(this.scope));
  }

  get problemLabel(): string {
    return this.problemCount === 0
      ? this.$t("admin.pdfTemplates.noProblems").toString()
      : this.$tc("admin.pdfTemplates.problemValues", this.problemCount).toString();
  }

  get lastSavedLabel(): string {
    const millis = this.template?.lastSavedMillis ?? null;
    return millis === null ? "–" : formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  filterName(filter: PdfTemplateFieldFilter): string {
    return this.$t(`admin.pdfTemplates.edit.filters.${filter}`).toString();
  }

  async onRowClicked(row: PdfTemplateEditorRow) {
    if (!this.isEditMode || !this.form) {
      return;
    }

    const mapping = await this.mappingDialog.show({
      row,
      catalogue: this.form.catalogue,
      options: this.options,
      storedCategoryLabel:
        this.template?.fields.find((field) => field.fieldName === row.fieldName)?.categoryLabel ?? null,
      showsCloudStatuses: this.isCertificateTemplate,
    });

    if (mapping !== null) {
      this.rows = this.rows.map((candidate) =>
        candidate.fieldName === row.fieldName ? { ...candidate, mapping } : candidate
      );
    }
  }

  /**
   * Takes over another template's mapping into the unsaved state: this scope's other templates, and
   * for a client also the shared ones. Read fresh, so a template saved meanwhile counts as it is now.
   */
  async onImport() {
    if (!this.template) {
      return;
    }

    let sources: MappingImportSource[];
    try {
      const own = await getClientPdfTemplatesForAdministration(this.isShared ? null : this.clientId);
      const shared = this.isShared ? null : await getClientPdfTemplatesForAdministration(null);
      sources = mappingImportSources(own.templates, shared?.templates ?? [], this.template.id);
    } catch {
      SnackbarModule.showError({ key: "admin.pdfTemplates.files.errors.FAILED" });
      return;
    }

    const choice = await this.importDialog.show(this.rows, sources);
    if (choice) {
      this.rows = applyMappingImport(this.rows, choice.source.mappings, choice.overwrite);
    }
  }

  /** SICHERN asks first, naming whose template it is and which fields change; BEARBEITEN does not. */
  async onSaveClicked() {
    if (this.isEditMode && this.hasChanged && !(await this.confirmSave())) {
      return;
    }
    this.onSave();
  }

  private async confirmSave(): Promise<boolean> {
    const parts = saveConfirmationParts(
      this.scope,
      this.title,
      describeMappingEdits(this.initialMappings, mappingsToSave(this.rows))
    );

    return this.confirmationDialogComponent.show({
      title: this.$t("admin.pdfTemplates.edit.confirmTitle").toString(),
      message: [this.translate(parts.scope), parts.changes.map((part) => this.translate([part])).join("\n")]
        .filter((paragraph) => paragraph !== "")
        .join("\n\n"),
      acceptMessage: this.$t("common.save").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
  }

  /** Sentences joined into one paragraph. */
  private translate(parts: PdfTemplateMessagePart[]): string {
    return parts.map((part) => this.$t(part.key, part.params).toString()).join(" ");
  }

  async doSave(): Promise<void> {
    if (!this.template) {
      return;
    }

    try {
      await saveClientPdfTemplateMappingForAdministration(
        this.isShared ? null : this.clientId,
        this.template.id,
        this.template.lastSavedMillis,
        mappingsToSave(this.rows)
      );
    } catch (error) {
      SnackbarModule.showError(saveErrorMessage(error));
      // Rethrown so the page stays in edit mode with the user's changes.
      throw error;
    }
  }

  /** Back to the list the template belongs to, also when the page was opened directly. */
  leavePage() {
    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES,
      query: { ...(this.clientId ? { clientId: this.clientId } : {}), ...(this.isShared ? { shared: "1" } : {}) },
    });
  }

  async load() {
    this.isLoadingTemplate = true;
    this.loadError = false;

    if (!this.isShared && this.clientId) {
      this.loadClientLabel(this.clientId);
    }

    try {
      this.form = await getClientPdfTemplateFormForAdministration(this.isShared ? null : this.clientId, this.templateId);
      this.rows = buildEditorRows(this.form.formFields, mappingsFromTemplateFields(this.form.template.fields));
      this.initialMappings = mappingsToSave(this.rows);
      this.initialKey = mappingsKey(this.initialMappings);
    } catch (error) {
      // Swallowed on purpose: the screen shows its own failure state ("not found" or "failed").
      const code = (error as { code?: unknown } | null)?.code;
      this.loadError = code !== "not-found" && code !== "functions/not-found";
      this.form = null;
      this.rows = [];
    } finally {
      this.isLoadingTemplate = false;
      this.updateToolbarTitle();
    }
  }

  /** "PDF-Vorlagen (geteilt) > name", or inside a client "Mandanten > client > PDF-Vorlagen > name". */
  updateToolbarTitle() {
    // Until the template has loaded it has no name to show (an empty part is left out).
    const name = this.template ? this.title : "";
    if (this.isShared) {
      ToolbarContentModule.setToolbarTitle(clientPageTitle(this.$t("admin.pdfTemplates.sharedTitle").toString(), null, name));
    } else {
      setClientPageToolbarTitle(this.clientId ?? "", this.$t("admin.pdfTemplates.title").toString(), name);
    }
  }

  /** For the banner and the confirmation; the client's id stands in when its name cannot be loaded. */
  private async loadClientLabel(clientId: string) {
    try {
      this.clientLabel = clientDetailTitle(await getClientForAdministration(clientId)) || null;
      // Opened by a link or reloaded, the app bar did not know the client yet.
      if (this.clientLabel) {
        rememberClientTitle(clientId, this.clientLabel);
        this.updateToolbarTitle();
      }
    } catch {
      // Swallowed on purpose: the id is shown instead, and the save itself does not depend on it.
      this.clientLabel = null;
    }
  }

  mounted() {
    this.updateToolbarTitle();
    this.load();
  }
}
